/**
 * QR Code Security Utilities
 * Handles secure QR code generation, validation, and expiration
 */

import { doc, updateDoc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';

export class QRCodeSecurity {
  /**
   * Generate a secure random QR code
   * @param {string} playerId - Player ID
   * @param {string} tournamentId - Tournament ID
   * @returns {Object} QR code data
   */
  static generateSecureQRCode(playerId, tournamentId) {
    // Generate a cryptographically secure random code
    const randomCode = this.generateRandomCode();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days from now
    
    return {
      code: randomCode,
      playerId: playerId,
      tournamentId: tournamentId,
      generatedAt: new Date(),
      expiresAt: expiresAt,
      isActive: true,
      generatedBy: 'system' // Will be updated with actual user info
    };
  }

  /**
   * Generate a cryptographically secure random code
   * @returns {string} Random 32-character code
   */
  static generateRandomCode() {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    
    for (let i = 0; i < 32; i++) {
      result += chars[array[i] % chars.length];
    }
    
    return result;
  }

  /**
   * Save QR code to database
   * @param {string} tournamentId - Tournament ID
   * @param {string} playerId - Player ID
   * @param {Object} qrCodeData - QR code data
   * @param {string} generatedBy - User who generated the code
   * @returns {Promise<Object>} Saved QR code data
   */
  static async saveQRCode(tournamentId, playerId, qrCodeData, generatedBy) {
    try {
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      
      // Update QR code data with generator info
      const qrCodeWithGenerator = {
        ...qrCodeData,
        generatedBy: generatedBy,
        generatedAt: new Date()
      };

      await updateDoc(tournamentRef, {
        [`qrCodes.${playerId}`]: qrCodeWithGenerator,
        [`lastUpdated.${playerId}`]: new Date()
      });

      return qrCodeWithGenerator;
    } catch (error) {
      console.error('Error saving QR code:', error);
      throw error;
    }
  }

  /**
   * Validate QR code and check expiration
   * @param {string} qrCode - QR code to validate
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<Object>} Validation result
   */
  static async validateQRCode(qrCode, tournamentId) {
    try {
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      
      if (!tournamentDoc.exists()) {
        return {
          valid: false,
          error: 'Tournament not found',
          playerId: null
        };
      }

      const tournamentData = tournamentDoc.data();
      const qrCodes = tournamentData.qrCodes || {};

      // Find the player with this QR code
      let playerId = null;
      let qrCodeData = null;

      for (const [pid, qrData] of Object.entries(qrCodes)) {
        if (qrData.code === qrCode) {
          playerId = pid;
          qrCodeData = qrData;
          break;
        }
      }

      if (!playerId || !qrCodeData) {
        return {
          valid: false,
          error: 'Invalid QR code',
          playerId: null
        };
      }

      // Check if QR code is active
      if (!qrCodeData.isActive) {
        return {
          valid: false,
          error: 'QR code has been deactivated',
          playerId: playerId
        };
      }

      // Check expiration
      const now = new Date();
      const expiresAt = qrCodeData.expiresAt?.toDate ? qrCodeData.expiresAt.toDate() : new Date(qrCodeData.expiresAt);

      if (now > expiresAt) {
        return {
          valid: false,
          error: 'QR code has expired',
          playerId: playerId,
          expired: true
        };
      }

      return {
        valid: true,
        error: null,
        playerId: playerId,
        qrCodeData: qrCodeData
      };

    } catch (error) {
      console.error('Error validating QR code:', error);
      return {
        valid: false,
        error: 'Validation error: ' + error.message,
        playerId: null
      };
    }
  }

  /**
   * Regenerate QR code for a player
   * @param {string} tournamentId - Tournament ID
   * @param {string} playerId - Player ID
   * @param {string} generatedBy - User regenerating the code
   * @returns {Promise<Object>} New QR code data
   */
  static async regenerateQRCode(tournamentId, playerId, generatedBy) {
    try {
      // Generate new QR code
      const newQRCode = this.generateSecureQRCode(playerId, tournamentId);
      
      // Save new QR code (this will replace the old one)
      const savedQRCode = await this.saveQRCode(tournamentId, playerId, newQRCode, generatedBy);
      
      return savedQRCode;
    } catch (error) {
      console.error('Error regenerating QR code:', error);
      throw error;
    }
  }

  /**
   * Deactivate QR code
   * @param {string} tournamentId - Tournament ID
   * @param {string} playerId - Player ID
   * @returns {Promise<void>}
   */
  static async deactivateQRCode(tournamentId, playerId) {
    try {
      const tournamentRef = doc(db, 'tournaments', tournamentId);
      
      await updateDoc(tournamentRef, {
        [`qrCodes.${playerId}.isActive`]: false,
        [`qrCodes.${playerId}.deactivatedAt`]: new Date()
      });
    } catch (error) {
      console.error('Error deactivating QR code:', error);
      throw error;
    }
  }

  /**
   * Get QR code status for a player
   * @param {string} tournamentId - Tournament ID
   * @param {string} playerId - Player ID
   * @returns {Promise<Object>} QR code status
   */
  static async getQRCodeStatus(tournamentId, playerId) {
    try {
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      
      if (!tournamentDoc.exists()) {
        return {
          exists: false,
          error: 'Tournament not found'
        };
      }

      const tournamentData = tournamentDoc.data();
      const qrCodeData = tournamentData.qrCodes?.[playerId];

      if (!qrCodeData) {
        return {
          exists: false,
          error: 'No QR code found for this player'
        };
      }

      const now = new Date();
      const expiresAt = qrCodeData.expiresAt?.toDate ? qrCodeData.expiresAt.toDate() : new Date(qrCodeData.expiresAt);
      const isExpired = now > expiresAt;
      const isActive = qrCodeData.isActive && !isExpired;

      return {
          exists: true,
          isActive: isActive,
          isExpired: isExpired,
          generatedAt: qrCodeData.generatedAt,
          expiresAt: expiresAt,
          generatedBy: qrCodeData.generatedBy,
          code: qrCodeData.code
        };

    } catch (error) {
      console.error('Error getting QR code status:', error);
      return {
        exists: false,
        error: 'Error retrieving QR code status: ' + error.message
      };
    }
  }

  /**
   * Generate QR code URL
   * @param {string} qrCode - QR code
   * @returns {string} Full URL for mobile scoring
   */
  static generateQRCodeURL(qrCode) {
    const baseUrl = window.location.origin;
    return `${baseUrl}/mobile-scoring/${qrCode}`;
  }

  /**
   * Clean up expired QR codes
   * @param {string} tournamentId - Tournament ID
   * @returns {Promise<number>} Number of codes cleaned up
   */
  static async cleanupExpiredQRCodes(tournamentId) {
    try {
      const tournamentDoc = await getDoc(doc(db, 'tournaments', tournamentId));
      
      if (!tournamentDoc.exists()) {
        return 0;
      }

      const tournamentData = tournamentDoc.data();
      const qrCodes = tournamentData.qrCodes || {};
      const now = new Date();
      let cleanedCount = 0;

      // Find expired codes
      const expiredPlayers = [];
      for (const [playerId, qrData] of Object.entries(qrCodes)) {
        const expiresAt = qrData.expiresAt?.toDate ? qrData.expiresAt.toDate() : new Date(qrData.expiresAt);
        if (now > expiresAt) {
          expiredPlayers.push(playerId);
        }
      }

      // Deactivate expired codes
      if (expiredPlayers.length > 0) {
        const tournamentRef = doc(db, 'tournaments', tournamentId);
        const updates = {};
        
        expiredPlayers.forEach(playerId => {
          updates[`qrCodes.${playerId}.isActive`] = false;
          updates[`qrCodes.${playerId}.deactivatedAt`] = new Date();
        });

        await updateDoc(tournamentRef, updates);
        cleanedCount = expiredPlayers.length;
      }

      return cleanedCount;
    } catch (error) {
      console.error('Error cleaning up expired QR codes:', error);
      return 0;
    }
  }
}

export default QRCodeSecurity;
